import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PublicPage, PublicHeader, BrandMark, StatusCard } from "@/components/public/PublicShell";
import { SharedCV, ShortlistCard } from "@/components/public/SharedCV";
import { ArrowLeft, Clock, FileText, Loader2, ShieldCheck, Unlink } from "lucide-react";

const ERRORS = {
  revoked: [Unlink, "This link was turned off", "The person who shared it turned this link off. Shared links are time-limited to protect candidate privacy."],
  expired: [Clock, "This link expired", "Shared links are time-limited to protect candidate privacy."],
  invalid: [Unlink, "This link isn't valid", "Check that you opened the whole link."],
};

export default function SharedProfile() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    fetch(`${BACKEND_URL}/api/public/shared/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "This link is no longer available");
        return r.json();
      })
      .then(setData).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  }, [token]);

  const candidates = data?.candidates || [];
  const isList = data?.kind === "shortlist";
  const validUntil = data?.valid_until ? new Date(data.valid_until).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;
  const errKind = /revoked/i.test(err || "") ? "revoked" : /expired/i.test(err || "") ? "expired" : "invalid";
  const [ErrIcon, errTitle, errText] = ERRORS[errKind];
  const meta = data && (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="shared-meta">
      <ShieldCheck className="h-3.5 w-3.5 text-primary shrink-0" />
      Read-only {isList ? "shortlist" : "profile"}{data.shared_by ? ` · shared by ${data.shared_by}` : ""}
      {validUntil && <span data-testid="shared-valid-until">· valid until {validUntil}</span>}
    </span>
  );

  return (
    <PublicPage>
      <PublicHeader right={<span className="hidden sm:block">{meta}</span>}><BrandMark /></PublicHeader>
      <main className="mx-auto max-w-[1180px] px-4 sm:px-8 py-10">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-24 justify-center" data-testid="shared-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : err ? (
          <div className="flex justify-center py-16">
            <StatusCard icon={ErrIcon} title={errTitle} testId="shared-error" actions={<Button variant="outline" asChild><Link to="/" data-testid="shared-home">Go to HireStream</Link></Button>}>
              <span data-kind={errKind}>{errText} Ask the person who shared it for a new link.</span>
            </StatusCard>
          </div>
        ) : (
          <div className="space-y-8" data-testid="shared-profile">
            <div className="sm:hidden">{meta}</div>
            {candidates.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground" data-testid="shared-empty"><FileText className="h-10 w-10 mx-auto mb-3 opacity-50" /><p>No published profiles in this link.</p></div>
            ) : isList && open == null ? (
              <>
                <div className="space-y-1.5">
                  {data.shared_by && <p className="text-sm text-muted-foreground">Shared by {data.shared_by}</p>}
                  <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Shortlist · {candidates.length} candidate{candidates.length === 1 ? "" : "s"}</h1>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{candidates.map((c, i) => <ShortlistCard key={i} c={c} i={i} onOpen={() => { setOpen(i); window.scrollTo({ top: 0 }); }} />)}</div>
              </>
            ) : (
              <div data-testid={isList ? `shared-detail-${open}` : "shared-candidate-0"} className="space-y-6">
                {isList && <button onClick={() => setOpen(null)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="shared-back"><ArrowLeft className="h-4 w-4" />Back to shortlist</button>}
                <SharedCV c={candidates[isList ? open : 0]} />
              </div>
            )}
          </div>
        )}
      </main>
    </PublicPage>
  );
}
